import { computed, type Ref } from "vue";
import { storeToRefs } from "pinia";
import { useResultsV2Store } from "@stores/survey/resultsV2/resultsV2";
import { useSurveyStore } from "@stores/survey/survey";
import { useSurveyLocalesStore } from "@stores/survey/surveyLocales";
import { useViewPopulations } from "@/composables/useViewPopulations";
import { POPULATION_AUTO_COLORS } from "@/lib/chartPalettes";

interface PopulationChoice {
  token: string;
  label: string;
}

export function useAnalysisContext() {
  const { availableCards, currentView, savedPopulations } = storeToRefs(useResultsV2Store());
  const { areWeightsEnabled } = storeToRefs(useSurveyStore());
  const { analysisLocale } = storeToRefs(useSurveyLocalesStore());

  const weighted = computed(() => {
    if (!areWeightsEnabled.value) return false;
    return currentView.value?.options?.apply_weightings ?? true;
  });

  const displayLocale = computed(
    () => currentView.value?.options?.locale_token || analysisLocale.value?.token || null,
  );

  return {
    availableCards,
    weighted,
    displayLocale,
    savedPopulations,
  };
}

// The one population a fetch-based analysis card computes on (options.population):
// the same displayed populations as question cards, plus its filters for the endpoint.
export function useCardPopulation(card: Ref<{ options?: { population?: string; color?: string } | null }>) {
  const { displayedPopulations, selectedPopulation } = useViewPopulations(card);

  const choices = computed<PopulationChoice[]>(() =>
    displayedPopulations.value.map((pop) => ({ token: pop.token, label: pop.label })),
  );
  const selected = computed(() => selectedPopulation.value?.token || "all");
  const filters = computed<Record<string, unknown>>(() => selectedPopulation.value?.filters || {});
  const color = computed(() => selectedPopulation.value?.color || POPULATION_AUTO_COLORS[0]);

  return { choices, selected, filters, color };
}

// n shades of one accent color, strongest first, mixed towards white only half-way so white text stays readable.
export function shadeRamp(hex: string, n: number): string[] {
  const rgb = [hex.slice(1, 3), hex.slice(3, 5), hex.slice(5, 7)].map((part) => parseInt(part, 16));
  return Array.from({ length: Math.max(n, 1) }, (_, i) => {
    const t = n <= 1 ? 0 : (i / (n - 1)) * 0.5;
    const mixed = rgb.map((channel) => Math.round(channel + (255 - channel) * t));
    return `rgb(${mixed[0]}, ${mixed[1]}, ${mixed[2]})`;
  });
}

export function isLightColor(color: string): boolean {
  const parts = color.startsWith("#")
    ? [color.slice(1, 3), color.slice(3, 5), color.slice(5, 7)].map((part) => parseInt(part, 16))
    : (color.match(/\d+/g) || []).slice(0, 3).map(Number);
  if (parts.length < 3 || parts.some(Number.isNaN)) return false;
  const [r, g, b] = parts.map((channel) => {
    const c = channel / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b > 0.45;
}

interface MatrixSpecs {
  matrix_type?: string;
  rating_limits?: { min?: number; max?: number };
  answers?: { code?: string; title?: unknown }[];
}

export function matrixScale(specs: MatrixSpecs | null | undefined): { min: number; max: number } {
  if (specs?.matrix_type === "nps") return { min: 0, max: 10 };
  if (specs?.matrix_type === "rating") {
    return { min: specs?.rating_limits?.min ?? 1, max: specs?.rating_limits?.max ?? 10 };
  }
  // single-type (Likert) matrices: answer codes 1..N form the ordinal scale.
  return { min: 1, max: Math.max(specs?.answers?.length || 5, 2) };
}

export function matrixCodes(
  specs: MatrixSpecs | null | undefined,
  resolveTitle: (title: unknown) => string,
): { code: string; label: string }[] {
  if (!specs) return [];
  if (["single", "multiple"].includes(specs.matrix_type || "")) {
    return (specs.answers || []).map((answer, idx) => {
      const code = String(answer.code || idx + 1);
      return { code, label: resolveTitle(answer.title) || code };
    });
  }
  const { min, max } = matrixScale(specs);
  const codes = [];
  for (let n = min; n <= max; n++) codes.push({ code: String(n), label: String(n) });
  return codes;
}

const MULTIPLE_TYPES = ["q_multiple", "q_image_choice_multiple"];
const MULTIPLE_RECODES = ["Multiple Match (Closed)", "User attribute (Multiple)", "Conditions (Multiple)"];

export function isMultipleChoiceCard(card: {
  type?: string;
  questions?: unknown[];
  specs?: { matrix_type?: string; transformation_type?: string } | null;
}): boolean {
  if (!card.questions?.length) return false;
  if (card.type === "q_matrix") return card.specs?.matrix_type === "multiple";
  if (card.type === "q_hidden") return MULTIPLE_RECODES.includes(card.specs?.transformation_type || "");
  return MULTIPLE_TYPES.includes(card.type || "");
}

export function closedQuestion<Q extends { question_type?: string }>(meta: { questions?: Q[] } | null | undefined): Q | null {
  return (meta?.questions || []).find((q) => q.question_type !== "open") || null;
}

export function cardCodes(
  meta: { type?: string; specs?: MatrixSpecs | null } | null | undefined,
  resolveTitle: (title: unknown) => string,
): { code: string; label: string }[] {
  if (!meta) return [];
  const specs = meta.specs || {};
  if (meta.type === "q_nps") return matrixCodes({ matrix_type: "nps" }, resolveTitle);
  if (meta.type === "q_rating") return matrixCodes({ matrix_type: "rating", rating_limits: specs.rating_limits }, resolveTitle);
  const matrixType = meta.type === "q_matrix" ? specs.matrix_type || "single" : "single";
  return matrixCodes({ ...specs, matrix_type: matrixType }, resolveTitle);
}
